import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { createFileRoute } from '@tanstack/react-router'
import {
  Archive,
  Check,
  CheckCheck,
  ChevronLeft,
  Code,
  Filter,
  Info,
  Mail,
  MessageSquare,
  MoreVertical,
  Phone,
  Search,
  Send,
  Star
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

export const Route = createFileRoute('/_protected/dashboard/messages')({
  component: MessagesPage,
})

// Mock data for demonstration
const mockConversations = [
  {
    id: '1',
    contact: {
      name: 'Sarah Johnson',
      avatar: '/api/placeholder/40/40',
      email: 'sarah.j@email.com',
      phone: '(555) 123-4567',
      type: 'Buyer',
      status: 'online'
    },
    lastMessage: {
      text: 'Hi! I\'m very interested in viewing this property. Are you available for a showing this weekend?',
      timestamp: '2m ago',
      isRead: false,
      isFromSender: false
    },
    unreadCount: 2,
    isStarred: true
  },
  {
    id: '2',
    contact: {
      name: 'Michael Chen',
      avatar: '/api/placeholder/40/40',
      email: 'mchen@realty.com',
      phone: '(555) 987-6543',
      type: 'Agent',
      status: 'away'
    },
    lastMessage: {
      text: 'Thank you for the additional photos. My client would like to schedule a viewing.',
      timestamp: '1h ago',
      isRead: true,
      isFromSender: false
    },
    unreadCount: 0,
    isStarred: false
  },
  {
    id: '3',
    contact: {
      name: 'Emma Rodriguez',
      avatar: '/api/placeholder/40/40',
      email: 'emma.r@gmail.com',
      phone: '(555) 456-7890',
      type: 'Buyer',
      status: 'offline'
    },
    lastMessage: {
      text: 'Perfect, I\'ll see you tomorrow at 2 PM for the viewing.',
      timestamp: '3h ago',
      isRead: true,
      isFromSender: true
    },
    unreadCount: 0,
    isStarred: false
  },
  {
    id: '4',
    contact: {
      name: 'David Kim',
      avatar: '/api/placeholder/40/40',
      email: 'dkim@outlook.com',
      phone: '(555) 234-5678',
      type: 'Buyer',
      status: 'online'
    },
    lastMessage: {
      text: 'Is the property still available? I\'d love to take a look.',
      timestamp: '1d ago',
      isRead: true,
      isFromSender: false
    },
    unreadCount: 0,
    isStarred: false
  }
]

const mockMessages = [
  {
    id: '1',
    text: 'Hi! I\'m very interested in viewing this property. Are you available for a showing this weekend?',
    timestamp: '10:30 AM',
    isFromSender: false,
    status: 'delivered'
  },
  {
    id: '2',
    text: 'I\'ve been looking for a property in this area for months and this one looks perfect for my family.',
    timestamp: '10:31 AM',
    isFromSender: false,
    status: 'delivered'
  },
  {
    id: '3',
    text: 'Hello Sarah! Thank you for your interest. I\'d be happy to arrange a viewing. I have availability on Saturday afternoon or Sunday morning. Which works better for you?',
    timestamp: '11:15 AM',
    isFromSender: true,
    status: 'read'
  },
  {
    id: '4',
    text: 'Saturday afternoon would be perfect! What time would work best?',
    timestamp: '11:45 AM',
    isFromSender: false,
    status: 'delivered'
  },
  {
    id: '5',
    text: 'How about 2:00 PM? I can meet you at the property and give you a full tour.',
    timestamp: '12:00 PM',
    isFromSender: true,
    status: 'read'
  }
]

/**
 * MessagesPage Component - Dashboard Messages Interface
 * 
 * DEV NOTE: This messaging interface is still under development. 
 * We're building this feature primarily to collect email contacts from potential buyers,
 * though it may see limited use until we implement more comprehensive buyer tools and features.
 * 
 * Current status: Mock data and UI implementation - backend integration pending
 */
function MessagesPage() {
  const [selectedConversation, setSelectedConversation] = useState(mockConversations[0])
  const [messageText, setMessageText] = useState('')
  const [showSidebar, setShowSidebar] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const handleSendMessage = () => {
    if (messageText.trim()) {
      // In a real app, this would send the message
      setMessageText('')
      // Auto-resize textarea back to original size
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto'
      }
    }
  }

  const handleSelectConversation = (conversation: typeof mockConversations[0]) => {
    setSelectedConversation(conversation)
    setShowSidebar(false)
  }

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`
    }
  }, [messageText])

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [selectedConversation])

  const filteredConversations = mockConversations.filter(conv =>
    conv.contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    conv.lastMessage.text.toLowerCase().includes(searchQuery.toLowerCase())
  )

  return (
    <div className="h-full bg-linear-to-br from-slate-50 to-white">
      <div className="flex h-full max-w-7xl mx-auto bg-white shadow-sm border border-slate-200/60 rounded-xl overflow-hidden">
        {/* Mobile Sidebar Overlay */}
        {showSidebar && (
          <div 
            className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 md:hidden transition-all duration-300"
            onClick={() => setShowSidebar(false)}
          />
        )}
        
        {/* Conversations Sidebar */}
        <div className={cn(
          "md:relative md:translate-x-0 md:w-80 md:shrink-0",
          "fixed top-0 left-0 h-full w-80 max-w-[85vw] z-50 transition-all duration-300 ease-out",
          showSidebar ? "translate-x-0" : "-translate-x-full md:translate-x-0",
          "bg-white border-r border-slate-200/80 flex flex-col"
        )}>
          {/* Header */}
          <div className="shrink-0 p-6 border-b border-slate-200/80 bg-slate-50/50">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-xl font-semibold text-slate-900 tracking-tight">Messages</h1>
                <p className="text-sm text-slate-500 mt-0.5">
                  {filteredConversations.length} conversation{filteredConversations.length !== 1 ? 's' : ''}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="text-slate-500 hover:text-slate-700 hover:bg-slate-100 h-8 w-8 transition-colors"
                >
                  <Filter className="h-4 w-4" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="text-slate-500 hover:text-slate-700 hover:bg-slate-100 h-8 w-8 transition-colors md:hidden"
                  onClick={() => setShowSidebar(false)}
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </div>
            </div>
            
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
              />
            </div>
          </div>

          {/* Conversations List */}
          <div className="flex-1 overflow-hidden">
            <ScrollArea className="h-full">
              <div className="p-3 space-y-1">
                {filteredConversations.map((conversation) => (
                  <div
                    key={conversation.id}
                    onClick={() => handleSelectConversation(conversation)}
                    className={cn(
                      "group p-4 rounded-xl cursor-pointer transition-all duration-200 border",
                      "hover:bg-slate-50 hover:shadow-sm",
                      selectedConversation.id === conversation.id 
                        ? "bg-blue-50 border-blue-200/60 shadow-sm" 
                        : "border-transparent hover:border-slate-200/60"
                    )}
                  >
                    <div className="flex items-start gap-3">
                      <div className="relative shrink-0">
                        <Avatar className="h-11 w-11 ring-2 ring-white shadow-sm">
                          <AvatarImage src={conversation.contact.avatar} />
                          <AvatarFallback className="bg-linear-to-br from-blue-500 to-blue-600 text-white font-medium text-sm">
                            {conversation.contact.name.split(' ').map(n => n[0]).join('')}
                          </AvatarFallback>
                        </Avatar>
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2 min-w-0">
                            <h3 className="font-medium text-slate-900 truncate text-sm">
                              {conversation.contact.name}
                            </h3>
                            {conversation.isStarred && (
                              <Star className="h-3 w-3 text-amber-500 fill-amber-500 shrink-0" />
                            )}
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="text-xs text-slate-500 font-medium">
                              {conversation.lastMessage.timestamp}
                            </span>
                            {conversation.unreadCount > 0 && (
                              <div className="bg-blue-600 text-white text-xs px-1.5 py-0.5 rounded-full min-w-5 h-5 flex items-center justify-center font-medium">
                                {conversation.unreadCount}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div className="mb-2">
                          <Badge 
                            variant="outline" 
                            className="text-xs h-5 px-2 font-medium text-slate-500 border-slate-200 bg-slate-50"
                          >
                            {conversation.contact.type}
                          </Badge>
                        </div>
                        
                        <p className="text-sm text-slate-600 line-clamp-2 leading-relaxed">
                          {conversation.lastMessage.isFromSender && (
                            <span className="text-slate-400 mr-1">You:</span>
                          )}
                          {conversation.lastMessage.text}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        </div>

        {/* Message View */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {selectedConversation ? (
            <>
              {/* Message Header */}
              <div className="shrink-0 p-6 border-b border-slate-200/80 bg-white/80 backdrop-blur-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 min-w-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="md:hidden text-slate-500 hover:text-slate-700 hover:bg-slate-100 h-8 w-8 shrink-0 transition-colors"
                      onClick={() => setShowSidebar(true)}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    
                    <div className="relative">
                      <Avatar className="h-10 w-10 ring-2 ring-white shadow-sm shrink-0">
                        <AvatarImage src={selectedConversation.contact.avatar} />
                        <AvatarFallback className="bg-linear-to-br from-blue-500 to-blue-600 text-white font-medium">
                          {selectedConversation.contact.name.split(' ').map(n => n[0]).join('')}
                        </AvatarFallback>
                      </Avatar>
                    </div>
                    
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h2 className="font-semibold text-slate-900 text-lg truncate tracking-tight">
                          {selectedConversation.contact.name}
                        </h2>
                        <Badge 
                          variant="outline" 
                          className="text-xs h-5 px-2 font-medium text-slate-500 border-slate-200 bg-slate-50 shrink-0"
                        >
                          {selectedConversation.contact.type}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-500">
                        <span className="flex items-center gap-1.5 truncate">
                          <Mail className="h-3.5 w-3.5 shrink-0" />
                          <span className="truncate">{selectedConversation.contact.email}</span>
                        </span>
                        <span className="flex items-center gap-1.5 shrink-0">
                          <Phone className="h-3.5 w-3.5" />
                          {selectedConversation.contact.phone}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-1 shrink-0">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="text-slate-500 hover:text-slate-700 hover:bg-slate-100 h-9 w-9 transition-colors"
                    >
                      <Star className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="text-slate-500 hover:text-slate-700 hover:bg-slate-100 h-9 w-9 transition-colors"
                    >
                      <Info className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="text-slate-500 hover:text-slate-700 hover:bg-slate-100 h-9 w-9 transition-colors"
                    >
                      <Archive className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Developer Note - Visible to Dev Team */}
              <div className="shrink-0 mx-6 mt-4 mb-2">
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 border-l-4 border-l-purple-500">
                  <div className="flex items-start gap-3">
                    <div className="shrink-0">
                      <Code className="h-5 w-5 text-purple-600 mt-0.5" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-mono font-semibold text-purple-800 mb-1">
                        🔧 ANDREY NOTE
                      </h3>
                      <p className="text-sm text-purple-700 leading-relaxed font-mono">
                        If we have a message button on the listing, we could take their email and name without forcing buyers to make an account, we'll send it right to their email and put it here for visability, just a nothing feature to look better
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-hidden bg-linear-to-b from-slate-50/50 to-white">
                <ScrollArea className="h-full">
                  <div className="p-6">
                    <div className="space-y-6 max-w-4xl mx-auto">
                      {mockMessages.map((message, index) => (
                        <div
                          key={message.id}
                          className={cn(
                            "flex animate-in slide-in-from-bottom-2 duration-300",
                            message.isFromSender ? "justify-end" : "justify-start"
                          )}
                          style={{ animationDelay: `${index * 50}ms` }}
                        >
                          <div
                            className={cn(
                              "group max-w-md px-4 py-3 rounded-2xl relative transition-all duration-200",
                              message.isFromSender
                                ? "bg-blue-600 text-white ml-12 shadow-lg shadow-blue-600/20"
                                : "bg-white text-slate-900 mr-12 shadow-sm border border-slate-200/60 hover:shadow-md"
                            )}
                          >
                            <p className="text-sm leading-relaxed break-words">{message.text}</p>
                            <div className="flex items-center justify-between mt-2 gap-2">
                              <span
                                className={cn(
                                  "text-xs font-medium",
                                  message.isFromSender ? "text-blue-100" : "text-slate-500"
                                )}
                              >
                                {message.timestamp}
                              </span>
                              {message.isFromSender && (
                                <div className="shrink-0">
                                  {message.status === 'read' ? (
                                    <CheckCheck className="h-3.5 w-3.5 text-blue-200" />
                                  ) : (
                                    <Check className="h-3.5 w-3.5 text-blue-200" />
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>
                  </div>
                </ScrollArea>
              </div>

              {/* Message Composer */}
              <div className="shrink-0 p-6 border-t border-slate-200/80 bg-white/80 backdrop-blur-sm">
                <div className="max-w-4xl mx-auto">
                  <div className="flex gap-3 items-end">
                    <div className="flex-1 relative">
                      <Textarea
                        ref={textareaRef}
                        placeholder="Type your message..."
                        value={messageText}
                        onChange={(e) => setMessageText(e.target.value)}
                        className="min-h-[48px] max-h-[120px] resize-none border-slate-200 bg-white shadow-sm rounded-2xl px-4 py-3 pr-12 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault()
                            handleSendMessage()
                          }
                        }}
                      />
                    </div>
                    <Button
                      onClick={handleSendMessage}
                      disabled={!messageText.trim()}
                      size="icon"
                      className={cn(
                        "h-12 w-12 rounded-xl shadow-lg transition-all duration-200 shrink-0",
                        messageText.trim() 
                          ? "bg-blue-600 hover:bg-blue-700 hover:shadow-xl hover:shadow-blue-600/25 scale-100" 
                          : "bg-slate-300 cursor-not-allowed scale-95"
                      )}
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-3 text-center">
                    Press <kbd className="px-1.5 py-0.5 bg-slate-100 rounded text-xs font-medium">Enter</kbd> to send • 
                    <kbd className="px-1.5 py-0.5 bg-slate-100 rounded text-xs font-medium ml-1">Shift + Enter</kbd> for new line
                  </p>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center p-4 bg-linear-to-b from-slate-50/50 to-white">
              <div className="text-center max-w-md animate-in fade-in-0 zoom-in-95 duration-500">
                <div className="h-20 w-20 bg-linear-to-br from-blue-100 to-blue-200 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-sm">
                  <MessageSquare className="h-10 w-10 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2 tracking-tight">
                  Select a conversation
                </h3>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Choose a conversation from the sidebar to start messaging with your clients and agents
                </p>
                <Button
                  variant="outline"
                  className="md:hidden shadow-sm hover:shadow-md transition-shadow"
                  onClick={() => setShowSidebar(true)}
                >
                  View Conversations
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
} 